import { GCInstant as platform } from 'src/lib/gcinstant';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { PetSpeciesID, PetStatusEffect } from 'src/replicant/pet/pet.ruleset';
import { RecordOfAllBut, WithId } from 'src/replicant/utils/types';
import { State } from 'src/replicant/State';
import { syncPetState } from './pet.util';
import { AnimationState } from 'src/replicant/pet/PetAssets';

export type Attributes = {
  name: string;
  age: number;
  love: number;
  stomach: number;
  dirtiness: number;
  urine: number;
  toothAcidity: number;
};

export type PetState = {
  species: PetSpeciesID | undefined;
  statusEffects: Partial<Record<PetStatusEffect, boolean>>;
  action: AnimationState | undefined;
  attributes: Attributes;
};
export const initialAttributes: Attributes = {
  name: '',
  age: 0,
  love: 0,
  stomach: 0,
  dirtiness: 0,
  urine: 0,
  toothAcidity: 0,
};

const initialPetState: PetState = {
  species: undefined,
  statusEffects: {},
  attributes: initialAttributes,
  action: undefined,
};

const initialState = {} as RecordOfAllBut<
  string,
  typeof initialPetState,
  keyof typeof initialPetState
>;

const pet = createSlice({
  name: 'pet',

  initialState,

  reducers: {
    createPet: (
      state,
      { payload }: PayloadAction<WithId<Partial<PetState>>>,
    ) => {
      state[payload.id] = {
        ...initialPetState,
        ...(payload.payload ?? {}),
      };
    },

    setAttributes: (
      pet,
      { payload: { id, payload } }: PayloadAction<WithId<Partial<Attributes>>>,
    ) => {
      if (!pet[id]) {
        return;
      }

      Object.assign(pet[id].attributes, payload);
    },

    setSpecies(
      pet,
      {
        payload: { payload, id },
      }: PayloadAction<WithId<PetSpeciesID | undefined>>,
    ) {
      if (!pet[id]) {
        return;
      }

      pet[id].species = payload;
    },

    setStatusEffects(
      pet,
      {
        payload: { payload, id },
      }: PayloadAction<WithId<PetState['statusEffects']>>,
    ) {
      Object.assign(pet[id].statusEffects, payload);
    },

    setAction(
      pet,
      {
        payload: { payload, id },
      }: PayloadAction<WithId<AnimationState | undefined>>,
    ) {
      if (!pet[id]) {
        return;
      }
      pet[id].action = payload;
    },

    deletePet(pet, { payload }: PayloadAction<string>) {
      delete pet[payload];
    },
  },

  extraReducers: (builder) => {
    builder.addCase('RESET_APP', (_, action) => ({
      [platform.playerID]: syncPetState(
        initialPetState,
        (action as any).payload as State,
      ),
    }));
  },
});

export const petActions = pet.actions;
export default pet.reducer;
